// MobileTasksA — Variation A · Vibrant mobile Tasks list.
// Rendered by pages/Tasks.js when viewport <768px AND user is admin/super_admin.
//
// Design source: public/design/dnexus-mobile-crm.html  (lines 295-385)

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import MobileBottomTabBar from "./MobileBottomTabBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FONT = "'Plus Jakarta Sans', system-ui, sans-serif";

// Inject input CSS (shared visual style with MobileLeadsA's sheet)
if (typeof document !== "undefined" && !document.getElementById("__mobile_tasks_a_input_css__")) {
  const s = document.createElement("style");
  s.id = "__mobile_tasks_a_input_css__";
  s.textContent = `
    .mtasksa-input {
      width: 100%; padding: 11px 14px; border: 1px solid #ECECF3; border-radius: 12px;
      background: #F9F9FC; color: #1B1F2A; font-size: 14px; font-weight: 500;
      outline: none; font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    }
    .mtasksa-input:focus { border-color: #6366F1; background: #fff; box-shadow: 0 0 0 3px rgba(99,102,241,.12); }
  `;
  document.head.appendChild(s);
}

// ─── helpers ────────────────────────────────────────────────────────────────

function initialsOf(name) {
  return String(name || "??")
    .split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "??";
}
const GRADIENTS = [
  "linear-gradient(135deg,#818CF8,#6366F1)",
  "linear-gradient(135deg,#FBBF24,#F97316)",
  "linear-gradient(135deg,#38BDF8,#0EA5E9)",
  "linear-gradient(135deg,#34D399,#10B981)",
  "linear-gradient(135deg,#FB7185,#F43F5E)",
];
function gradientFor(name) {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
}

function priorityPill(p) {
  const s = String(p || "").toLowerCase();
  if (s === "high" || s === "urgent" || s === "critical") return { bg: "#FFF1F2", fg: "#E11D48", border: "#E11D48", label: "High" };
  if (s === "low") return { bg: "#E6F6FD", fg: "#0369A1", border: "#CBD0DA", label: "Low" };
  return { bg: "#FEF4E6", fg: "#B45309", border: "#CBD0DA", label: s ? (s[0].toUpperCase() + s.slice(1)) : "Medium" };
}

function statusBucket(t) {
  const s = String(t.status || "").toLowerCase();
  if (s === "to_do" || s === "pending" || s === "todo") return "todo";
  if (s === "doing" || s === "in_progress" || s === "in-progress") return "doing";
  if (s === "done" || s === "completed" || s === "pending_approval") return "done";
  return "todo";
}

function formatDueDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dayTime = new Date(dt); dayTime.setHours(0, 0, 0, 0);
  if (dayTime.getTime() === today.getTime()) {
    return `Today · ${dt.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}`;
  }
  if (dayTime.getTime() === tomorrow.getTime()) return "Tomorrow";
  return dt.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
}

const SEGMENTS = [
  { key: "todo",  label: "To-Do" },
  { key: "doing", label: "In Progress" },
  { key: "done",  label: "Done" },
];

// ─── Main component ─────────────────────────────────────────────────────────

export default function MobileTasksA({
  tasks = [],
  loading = false,
  onTaskClick,
  onAddTask,        // optional override; default opens the inline sheet
  onToggleDone,
  onTaskCreated,    // called after a successful POST so the parent can refetch
  teamMembers = [],
  projects = [],
  businessId,
  currentUser,
}) {
  const navigate = useNavigate();
  const [segment, setSegment] = useState("todo");
  const [addOpen, setAddOpen] = useState(false);

  const buckets = useMemo(() => {
    const out = { todo: [], doing: [], done: [] };
    for (const t of tasks) out[statusBucket(t)].push(t);
    return out;
  }, [tasks]);

  // Counts for the summary tiles
  const counts = useMemo(() => {
    const monthStart = new Date();
    monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
    let thisMonth = 0;
    for (const t of tasks) {
      const c = t.created_at || t.updated_at;
      if (c && new Date(c) >= monthStart) thisMonth++;
    }
    return {
      todo: buckets.todo.length,
      doing: buckets.doing.length,
      thisMonth,
    };
  }, [tasks, buckets]);

  const visible = useMemo(() => {
    const list = buckets[segment] || [];
    return [...list].sort((a, b) => {
      const ad = new Date(a.due_date || 0).getTime();
      const bd = new Date(b.due_date || 0).getTime();
      return ad - bd;
    });
  }, [buckets, segment]);

  const dateLabel = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div
      className="bg-[#F6F6FB] flex flex-col"
      style={{
        margin: "-1rem -1rem 0 -1rem",
        minHeight: "calc(100vh - 4rem)",
        fontFamily: FONT,
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
      }}
      data-testid="mobile-tasks-a"
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 6px" }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#1B1F2A", letterSpacing: "-.5px" }}>Tasks</div>
          <div style={{ fontSize: 12, color: "#8A8F9C", fontWeight: 600 }}>Today · {dateLabel}</div>
        </div>
        <button
          onClick={() => onAddTask ? onAddTask() : setAddOpen(true)}
          style={{
            width: 40, height: 40, borderRadius: 13,
            background: "linear-gradient(135deg,#818CF8,#6366F1)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 8px 16px rgba(99,102,241,.32)", border: "none",
          }}
          aria-label="Add task"
          data-testid="mobile-tasks-add-btn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/></svg>
        </button>
      </div>

      {/* Summary tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 9, padding: "8px 16px 4px" }}>
        <div style={{ background: "linear-gradient(155deg,#818CF8,#6366F1)", borderRadius: 16, padding: 13, color: "#fff", boxShadow: "0 10px 18px rgba(99,102,241,.28)" }}>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.todo}</div>
          <div style={{ fontSize: 10.5, fontWeight: 700, opacity: .92 }}>To-Do</div>
        </div>
        <div style={{ background: "#fff", border: "1px solid #EFEFF5", borderRadius: 16, padding: 13 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#0EA5E9" }}>{counts.doing}</div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6B7280" }}>In Progress</div>
        </div>
        <div style={{ background: "#fff", border: "1px solid #EFEFF5", borderRadius: 16, padding: 13 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#10B981" }}>{counts.thisMonth}</div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6B7280" }}>This Month</div>
        </div>
      </div>

      {/* Segmented control */}
      <div style={{ display: "flex", gap: 6, background: "#ECECF3", borderRadius: 13, padding: 4, margin: "10px 16px 4px" }}>
        {SEGMENTS.map((s) => {
          const active = segment === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setSegment(s.key)}
              style={{
                flex: 1, textAlign: "center", fontSize: 12,
                fontWeight: active ? 800 : 700,
                color: active ? "#fff" : "#6B7280",
                background: active ? "#6366F1" : "transparent",
                borderRadius: 10, padding: "7px 0", border: "none",
              }}
              data-testid={`mobile-tasks-seg-${s.key}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Task list */}
      <div style={{ flex: 1, padding: "6px 16px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
        {loading && tasks.length === 0 ? (
          [0, 1, 2].map((i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid #EFEFF5", borderRadius: 16, padding: 13, height: 70, opacity: .6 }}>
              <div style={{ height: 12, background: "#E5E7EB", borderRadius: 4, width: "70%", marginBottom: 8 }} />
              <div style={{ height: 10, background: "#E5E7EB", borderRadius: 4, width: "40%" }} />
            </div>
          ))
        ) : visible.length === 0 ? (
          <div style={{ background: "#fff", border: "1px solid #EFEFF5", borderRadius: 16, padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1B1F2A", marginBottom: 4 }}>
              No tasks {segment === "todo" ? "to do" : segment === "doing" ? "in progress" : "completed"}
            </div>
            <div style={{ fontSize: 12, color: "#8A8F9C" }}>
              {segment === "todo" ? "Tap + to create a task." : "All caught up."}
            </div>
          </div>
        ) : (
          visible.map((task) => {
            const isDone = segment === "done";
            const prio = priorityPill(task.priority);
            const assignee = task.assigned_to_name || task.assignee_name || task.assigned_user_name || task.created_by_name || "";
            return (
              <div
                key={task.task_id || task._id}
                onClick={() => onTaskClick && onTaskClick(task.task_id || task._id)}
                style={{
                  background: "#fff", border: "1px solid #EFEFF5", borderRadius: 16, padding: 13,
                  display: "flex", alignItems: "flex-start", gap: 11,
                  boxShadow: "0 1px 3px rgba(17,17,40,.04)",
                  cursor: onTaskClick ? "pointer" : "default",
                  opacity: isDone ? .75 : 1,
                }}
                data-testid={`mobile-task-card-${task.task_id || task._id}`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onToggleDone) onToggleDone(task, isDone);
                  }}
                  aria-label="Toggle complete"
                  style={{
                    width: 22, height: 22, borderRadius: 7,
                    border: `2px solid ${isDone ? "#10B981" : (priorityPill(task.priority).fg === "#E11D48" ? "#E11D48" : "#CBD0DA")}`,
                    background: isDone ? "#10B981" : "#fff",
                    flexShrink: 0, marginTop: 1, padding: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {isDone && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 12l4 4 10-10" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 14, fontWeight: 700, color: "#1B1F2A",
                    textDecoration: isDone ? "line-through" : "none",
                    overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                    WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  }}>
                    {task.title || task.name || "Untitled task"}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10.5, fontWeight: 800, color: prio.fg, background: prio.bg, padding: "3px 8px", borderRadius: 999 }}>
                      {prio.label}
                    </span>
                    {task.due_date && (
                      <span style={{ fontSize: 11, color: "#9AA0AE", fontWeight: 600 }}>{formatDueDate(task.due_date)}</span>
                    )}
                  </div>
                </div>
                {assignee && (
                  <div
                    title={assignee}
                    style={{
                      width: 26, height: 26, borderRadius: "50%",
                      background: gradientFor(assignee),
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: 11, fontWeight: 800, flexShrink: 0,
                    }}
                  >
                    {initialsOf(assignee)}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add Task sheet */}
      <AddTaskSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        teamMembers={teamMembers}
        projects={projects}
        businessId={businessId}
        onCreated={() => { onTaskCreated && onTaskCreated(); }}
      />

      <MobileBottomTabBar active="tasks" />
    </div>
  );
}

// ─── Add Task bottom sheet (minimal, mobile-native) ─────────────────────────

function AddTaskSheet({ open, onClose, teamMembers = [], projects = [], businessId, onCreated }) {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [projectId, setProjectId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(""); setPriority("medium"); setDueDate(""); setAssignedTo(""); setProjectId(""); setSaving(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        priority,
        status: "to_do",
        health_status: "on_track",
        due_date: dueDate || undefined,
        assigned_to: assignedTo || undefined,
        project_id: projectId || undefined,
        business_id: businessId || undefined,
      };
      const r = await fetch(`${BACKEND_URL}/api/tasks/enhanced`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success("Task added");
        onClose();
        if (onCreated) onCreated();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || err.message || "Failed to create task");
      }
    } catch (_) {
      toast.error("Failed to create task");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end" style={{ background: "rgba(15,15,25,.45)" }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full bg-white rounded-t-3xl"
        style={{
          fontFamily: FONT,
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 18px)",
          boxShadow: "0 -16px 32px rgba(15,15,40,.18)",
          maxHeight: "85vh", overflowY: "auto",
        }}
      >
        <div style={{ width: 44, height: 5, borderRadius: 99, background: "#E5E7EB", margin: "10px auto 4px" }} />
        <div style={{ padding: "10px 20px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#1B1F2A" }}>Add Task</div>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", background: "#F2F3F6", display: "flex", alignItems: "center", justifyContent: "center", border: "none" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#5B5F6B" strokeWidth="2" strokeLinecap="round"/></svg>
            </button>
          </div>

          <SheetField label="Title *">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs to be done?" autoFocus className="mtasksa-input" />
          </SheetField>
          <SheetField label="Priority">
            <select value={priority} onChange={(e) => setPriority(e.target.value)} className="mtasksa-input">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </SheetField>
          <SheetField label="Due Date">
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mtasksa-input" />
          </SheetField>
          <SheetField label="Assign To">
            <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className="mtasksa-input">
              <option value="">Unassigned</option>
              {teamMembers.map((m) => <option key={m.user_id || m.id} value={m.user_id || m.id}>{m.name || m.email}</option>)}
            </select>
          </SheetField>
          {projects.length > 0 && (
            <SheetField label="Project">
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="mtasksa-input">
                <option value="">No project</option>
                {projects.map((p) => <option key={p.project_id || p.id} value={p.project_id || p.id}>{p.name || p.title}</option>)}
              </select>
            </SheetField>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="mobile-add-task-save"
            style={{
              width: "100%", marginTop: 18, padding: 14, borderRadius: 16,
              background: saving ? "#A5B4FC" : "linear-gradient(135deg,#818CF8,#6366F1)",
              color: "#fff", fontSize: 15, fontWeight: 800, border: "none",
              boxShadow: "0 12px 22px rgba(99,102,241,.32)",
            }}
          >
            {saving ? "Saving…" : "Add Task"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SheetField({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: ".3px", marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}
